import type { CartItem } from '../types/Product';
import type { ServiceResponse, PaginatedResponse, PaginationOptions } from './types';
import { apiRequest } from '../utils/api';
import { API_ENDPOINTS } from '../config/api.config';

// Backend response types
interface AddressDto {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface OrderItemDto {
  productId: string;
  productName: string;
  sku: string;
  quantity: number;
  price: number;
  subtotal: number;
  image?: string;
}

interface OrderItemResponseDto {
  productId: string;
  productName: string;
  productImage?: string;
  quantity: number;
  price: number;
  total: number;
  sku?: string;
}

interface OrderResponseDto {
  _id: string;
  id: string;
  orderNumber: string;
  customerId: string;
  customerName: string;
  customerEmail?: string;
  customerPhone: string;
  items: OrderItemResponseDto[];
  subtotal: number;
  tax: number;
  shipping: number;
  packing: number;
  discount: number;
  total: number;
  status: string;
  paymentMethod: string;
  paymentStatus: string;
  shippingAddress: AddressDto;
  billingAddress?: AddressDto;
  trackingNumber?: string;
  carrier?: string;
  notes?: string;
  couponCode?: string;
  refundAmount?: number;
  cancelReason?: string;
  deliveryDate?: string;
  source: string;
  createdAt: string;
  updatedAt: string;
  statusHistory?: Array<{
    status: string;
    timestamp: string;
    notes?: string;
    attachments?: Array<{
      url: string;
      name: string;
      type: string;
      size?: number;
    }>;
    updatedBy?: string;
    updatedByEmail?: string;
  }>;
  paymentProofs?: Array<{
    url: string;
    fileName?: string;
    fileType?: string;
    uploadedAt?: string;
    status?: string;
  }>;
}

interface CreateOrderDto {
  items: OrderItemDto[];
  shippingAddress: AddressDto;
  billingAddress?: AddressDto;
  paymentMethod: string;
  customerNotes?: string;
  internalNotes?: string;
  couponCode?: string;
  pickupLocation?: string;
  pickupInstructions?: string;
  source?: string;
  discount?: number;
  shipping?: number;
  tax?: number;
  packing?: number;
}

// Frontend types
export interface Address {
  id?: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  isDefault?: boolean;
}

export interface Order {
  id: string;
  orderNumber: string;
  userId: string;
  items: OrderItem[];
  shippingAddress: Address;
  billingAddress?: Address;
  subtotal: number;
  tax: number;
  shipping: number;
  packing: number;
  discount: number;
  total: number;
  status: OrderStatus;
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  trackingNumber?: string;
  carrier?: string;
  notes?: string;
  couponCode?: string;
  createdAt: Date;
  updatedAt: Date;
  statusHistory?: OrderStatusHistory[];
  paymentProofs?: Array<{
    url: string;
    fileName?: string;
    fileType?: string;
    uploadedAt?: string;
    status?: string;
  }>;
}

export interface OrderItem {
  productId: string;
  productName: string;
  productImage?: string;
  price: number;
  quantity: number;
  total: number;
  sku?: string;
}

export interface OrderStatusHistory {
  status: OrderStatus;
  timestamp: Date;
  notes?: string;
  attachments?: Array<{
    url: string;
    name: string;
    type: string;
    size?: number;
  }>;
  updatedBy?: string;
  updatedByEmail?: string;
}

export type OrderStatus = 
  | 'pending'
  | 'confirmed'
  | 'processing'
  | 'ready_for_pickup'
  | 'ready_for_delivery'
  | 'out_for_delivery'
  | 'shipped'
  | 'delivered'
  | 'completed'
  | 'cancelled'
  | 'refunded'
  | 'on_hold';

export type PaymentMethod = 'cash' | 'card' | 'upi' | 'net_banking' | 'wallet' | 'cheque' | 'mixed';
export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'refunded' | 'partially_refunded';

export interface CreateOrderData {
  items: CartItem[];
  shippingAddress: Address;
  billingAddress?: Address;
  paymentMethod: PaymentMethod;
  customerNotes?: string;
  pickupLocation?: string;
  pickupInstructions?: string;
  couponCode?: string;
  couponDiscount?: number;
  packagingCharge?: number;
}

export interface OrderTrackingResponse {
  order: Order;
  timeline: Array<{
    status: OrderStatus;
    timestamp: Date;
    description: string;
    notes?: string;
  }>;
}

class OrderService {
  // Transform backend order to frontend format
  private transformOrder(order: OrderResponseDto): Order {
    return {
      id: order._id || order.id,
      orderNumber: order.orderNumber,
      userId: order.customerId,
      items: order.items.map(item => ({
        productId: item.productId,
        productName: item.productName,
        productImage: item.productImage || (item as any).image || '',
        price: item.price,
        quantity: item.quantity,
        total: (item as any).subtotal || item.total || 0, // Backend returns 'subtotal' not 'total'
        sku: item.sku
      })),
      shippingAddress: order.shippingAddress,
      billingAddress: order.billingAddress,
      subtotal: order.subtotal,
      tax: order.tax,
      shipping: order.shipping,
      packing: order.packing || 0,
      discount: order.discount,
      total: order.total,
      status: order.status as OrderStatus,
      paymentMethod: order.paymentMethod as PaymentMethod,
      paymentStatus: order.paymentStatus as PaymentStatus,
      trackingNumber: order.trackingNumber,
      carrier: order.carrier,
      notes: order.notes,
      couponCode: order.couponCode,
      createdAt: new Date(order.createdAt),
      updatedAt: new Date(order.updatedAt),
      statusHistory: order.statusHistory?.map(history => ({
        status: history.status as OrderStatus,
        timestamp: new Date(history.timestamp),
        notes: history.notes,
        attachments: history.attachments,
        updatedBy: history.updatedBy,
        updatedByEmail: history.updatedByEmail
      })),
      paymentProofs: order.paymentProofs
    };
  }

  // Create new order
  async createOrder(orderData: CreateOrderData): Promise<ServiceResponse<Order>> {
    try {
      const user = JSON.parse(localStorage.getItem('auth_user') || '{}');
      if (!user.id) {
        throw new Error('User not authenticated');
      }

      // Transform cart items to order items
      const items: OrderItemDto[] = orderData.items.map(item => ({
        productId: item.id,
        productName: item.name,
        sku: item.sku || 'N/A',
        quantity: item.quantity,
        price: item.price,
        subtotal: item.price * item.quantity,
        image: item.image
      }));

      const createOrderDto: CreateOrderDto = {
        items,
        shippingAddress: orderData.shippingAddress,
        billingAddress: orderData.billingAddress,
        paymentMethod: orderData.paymentMethod,
        customerNotes: orderData.customerNotes,
        pickupLocation: orderData.pickupLocation,
        pickupInstructions: orderData.pickupInstructions,
        couponCode: orderData.couponCode,
        source: 'web',
        shipping: 0,
        tax: 0,
        discount: orderData.couponDiscount || 0,
        packing: orderData.packagingCharge || 0
      };

      const response = await apiRequest<OrderResponseDto>(
        API_ENDPOINTS.ORDERS.CREATE_MY_ORDER,
        {
          method: 'POST',
          body: JSON.stringify(createOrderDto),
        },
        true
      );

      return {
        success: true,
        data: this.transformOrder(response)
      };
    } catch (error) {
      console.error('Error creating order:', error);
      return {
        success: false,
        data: null as any,
        error: error instanceof Error ? error.message : 'Failed to create order'
      };
    }
  }

  // Get user orders
  async getUserOrders(
    pagination?: PaginationOptions
  ): Promise<ServiceResponse<PaginatedResponse<Order>>> {
    try {
      // Note: The /my-orders endpoint doesn't support pagination on backend
      // So we fetch all orders and do client-side pagination
      const response = await apiRequest<OrderResponseDto[]>(
        API_ENDPOINTS.ORDERS.MY_ORDERS,
        {},
        true
      );

      // Transform all orders
      const allOrders = response.map(order => this.transformOrder(order));
      
      // Apply client-side pagination
      const page = pagination?.page || 1;
      const limit = pagination?.limit || 10;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      
      const paginatedOrders = allOrders.slice(startIndex, endIndex);
      const totalPages = Math.ceil(allOrders.length / limit);

      return {
        success: true,
        data: {
          data: paginatedOrders,
          total: allOrders.length,
          page: page,
          limit: limit,
          totalPages: totalPages
        }
      };
    } catch (error) {
      console.error('Error fetching orders:', error);
      return {
        success: false,
        data: {
          data: [],
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0
        },
        error: error instanceof Error ? error.message : 'Failed to fetch orders'
      };
    }
  }

  // Get order by ID
  async getOrderById(orderId: string): Promise<ServiceResponse<Order | null>> {
    try {
      const response = await apiRequest<OrderResponseDto>(
        API_ENDPOINTS.ORDERS.BY_ID(orderId),
        {},
        true
      );

      return {
        success: true,
        data: this.transformOrder(response)
      };
    } catch (error) {
      console.error('Error fetching order:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Order not found'
      };
    }
  }

  // Get order by order number
  async getOrderByNumber(orderNumber: string): Promise<ServiceResponse<Order | null>> {
    try {
      const response = await apiRequest<OrderResponseDto>(
        API_ENDPOINTS.ORDERS.BY_NUMBER(orderNumber),
        {},
        true
      );

      return {
        success: true,
        data: this.transformOrder(response)
      };
    } catch (error) {
      console.error('Error fetching order by number:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Order not found'
      };
    }
  }

  // Cancel order
  async cancelOrder(orderId: string): Promise<ServiceResponse<Order>> {
    try {
      const response = await apiRequest<OrderResponseDto>(
        API_ENDPOINTS.ORDERS.CANCEL(orderId),
        {
          method: 'POST',
        },
        true
      );

      return {
        success: true,
        data: this.transformOrder(response)
      };
    } catch (error) {
      console.error('Error cancelling order:', error);
      return {
        success: false,
        data: null as any,
        error: error instanceof Error ? error.message : 'Failed to cancel order'
      };
    }
  }

  // Track order with status history
  async trackOrder(orderNumber: string): Promise<ServiceResponse<OrderTrackingResponse>> {
    try {
      // First get the order
      const orderResponse = await this.getOrderByNumber(orderNumber);
      if (!orderResponse.success || !orderResponse.data) {
        throw new Error('Order not found');
      }

      const order = orderResponse.data;

      // Get status history
      const historyResponse = await apiRequest<Array<{
        status: string;
        timestamp: string;
        notes?: string;
        attachments?: any[];
      }>>(
        API_ENDPOINTS.ORDERS.STATUS_HISTORY(order.id),
        {},
        true
      );

      // Build timeline from status history
      const timeline = historyResponse.map(history => ({
        status: history.status as OrderStatus,
        timestamp: new Date(history.timestamp),
        description: this.getStatusDescription(history.status as OrderStatus),
        notes: history.notes
      }));

      return {
        success: true,
        data: {
          order,
          timeline
        }
      };
    } catch (error) {
      console.error('Error tracking order:', error);
      return {
        success: false,
        data: null as any,
        error: error instanceof Error ? error.message : 'Failed to track order'
      };
    }
  }

  // Get order statistics
  async getOrderStatistics(fromDate?: Date, toDate?: Date): Promise<ServiceResponse<{
    totalOrders: number;
    totalRevenue: number;
    averageOrderValue: number;
    ordersByStatus: Record<string, number>;
  }>> {
    try {
      const params = new URLSearchParams();
      if (fromDate) {
        params.append('fromDate', fromDate.toISOString());
      }
      if (toDate) {
        params.append('toDate', toDate.toISOString());
      }

      const endpoint = `${API_ENDPOINTS.ORDERS.STATISTICS}?${params.toString()}`;
      const response = await apiRequest<any>(endpoint, {}, true);

      return {
        success: true,
        data: response
      };
    } catch (error) {
      console.error('Error fetching order statistics:', error);
      return {
        success: false,
        data: {
          totalOrders: 0,
          totalRevenue: 0,
          averageOrderValue: 0,
          ordersByStatus: {}
        },
        error: error instanceof Error ? error.message : 'Failed to fetch statistics'
      };
    }
  }

  private getStatusDescription(status: OrderStatus): string {
    const descriptions: Record<OrderStatus, string> = {
      pending: 'Order placed successfully',
      confirmed: 'Order confirmed and payment verified',
      processing: 'Order is being prepared',
      ready_for_pickup: 'Order is ready for pickup',
      ready_for_delivery: 'Order is ready for delivery',
      out_for_delivery: 'Order is out for delivery',
      shipped: 'Order has been shipped',
      delivered: 'Order delivered successfully',
      completed: 'Order completed',
      cancelled: 'Order cancelled',
      refunded: 'Order refunded',
      on_hold: 'Order is on hold'
    };
    return descriptions[status] || status;
  }

  // Upload payment proof
  async uploadPaymentProof(
    orderId: string, 
    paymentProof: {
      url: string;
      fileName: string;
      fileType: string;
      extractedData?: any;
    }
  ): Promise<ServiceResponse<Order>> {
    try {
      const response = await apiRequest<OrderResponseDto>(
        `${API_ENDPOINTS.ORDERS.BY_ID(orderId)}/payment-proof`,
        {
          method: 'POST',
          body: JSON.stringify({
            paymentProofUrl: paymentProof.url,
            paymentProofName: paymentProof.fileName,
            paymentProofType: paymentProof.fileType,
            extractedData: paymentProof.extractedData
          }),
        },
        true
      );

      return {
        success: true,
        data: this.transformOrder(response)
      };
    } catch (error) {
      console.error('Error uploading payment proof:', error);
      return {
        success: false,
        data: null as any,
        error: error instanceof Error ? error.message : 'Failed to upload payment proof'
      };
    }
  }

  // Check if transaction ID already exists
  async checkTransactionId(transactionId: string): Promise<ServiceResponse<{ exists: boolean; orderId?: string }>> {
    try {
      const response = await apiRequest<{ exists: boolean; orderId?: string }>(
        API_ENDPOINTS.ORDERS.CHECK_TRANSACTION_ID(transactionId),
        {
          method: 'GET',
        },
        true
      );

      return {
        success: true,
        data: response
      };
    } catch (error) {
      console.error('Error checking transaction ID:', error);
      return {
        success: false,
        data: { exists: false },
        error: error instanceof Error ? error.message : 'Failed to check transaction ID'
      };
    }
  }

  // Get order status history
  async getStatusHistory(orderId: string): Promise<ServiceResponse<any[]>> {
    try {
      const response = await apiRequest<any[]>(
        API_ENDPOINTS.ORDERS.STATUS_HISTORY(orderId),
        {},
        true
      );

      return {
        success: true,
        data: response
      };
    } catch (error) {
      console.error('Error fetching status history:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch status history'
      };
    }
  }
}

export const orderService = new OrderService();